// ============================================================
// TauxBloc — les deux taux, là où on regarde le prix.
// ------------------------------------------------------------
// On ne juge pas un temps sans savoir ce qu'il coûte, et aller chercher
// le taux dans un autre écran casse le raisonnement en deux. Ce bloc
// existe pour ça, et il reste juste au-dessus de la synthèse.
//
// ══════════════════════════════════════════════════════════════
// LES DEUX TAUX SE LISENT ICI, ILS SE CHANGENT DANS LES PARAMÈTRES
// ══════════════════════════════════════════════════════════════
//
// Ils étaient présentés comme MODIFIABLES sur la pièce. Ils ne
// l'étaient pas, et le contrôle était nuisible de trois façons à la
// fois — constaté le 26 août 2026 :
//
//  1. `pieces.taux_machine` et `pieces.taux_humain` N'EXISTENT PAS.
//     Il y a `taux_machine_applique` et `taux_humain_applique`, qui
//     sont la PHOTO du taux au chiffrage, pas une surcharge.
//  2. `pieceSchema` est strict. Toucher l'un des deux champs faisait
//     donc tomber l'enregistrement du DEVIS ENTIER en 400, avec un
//     message qui accuse la pièce et non le champ. C'est mot pour mot
//     le défaut du 12 août sur AFU00498, où un `total_ht` est resté
//     nul après une matinée de travail (§8, conventions backend).
//  3. Et même enregistré, il n'aurait rien changé : `computePiece` lit
//     les taux dans `settings.rates`, et nulle part ailleurs.
//
// > **Un champ qui ne peut pas marcher est pire qu'un champ absent :
// > il invite au geste qui casse.**
//
// La règle du §2bis n'a jamais dit autre chose : deux taux, GLOBAUX,
// un seul de chaque, et aucune machine — ni aucune pièce — ne porte de
// taux. Le bloc dit donc ce qui s'applique, d'où ça vient, et où on le
// change. Le taux retenu reste photographié sur la pièce au chiffrage :
// changer un taux ne modifie jamais un devis déjà chiffré.
//
// Le COEFFICIENT DE SÉCURITÉ, lui, est une vraie colonne de la pièce et
// une vraie décision par affaire (§6). Il reste modifiable.

import { Link } from 'react-router';
import NumberField from '../../ui/NumberField.jsx';
import { formatEuroOuTiret } from '../../lib/format.js';

export function TauxBloc({
  niveau = 'pièce',
  effectifs = {},
  disabled,
  coefficient = null,
  onCoefficient = null,
}) {
  const champs = [
    { clef: 'machine', label: 'Taux machine', valeur: effectifs.machine },
    { clef: 'humain', label: 'Taux humain', valeur: effectifs.humain },
  ];

  return (
    <div className="taux-bloc">
      <p className="caps">Taux horaires appliqués</p>

      {/* Une VALEUR AFFICHÉE, pas un champ. Un champ dit « écris ici » ;
          ces deux-là se règlent en un seul endroit pour tout
          l'atelier. */}
      <div className="grid-2">
        {champs.map((c) => (
          <div key={c.clef} className="taux-bloc__lu">
            <p className="caps">{c.label}</p>
            <p className="num taux-bloc__valeur">
              {c.valeur == null ? '—' : `${formatEuroOuTiret(c.valeur)}/h`}
            </p>
          </div>
        ))}
      </div>

      <p className="field__hint">
        Taux de <strong>vente</strong> — marge et temps morts d’atelier inclus, aucun
        coefficient ne s’y ajoute. Ils valent pour tout l’atelier et se changent dans{' '}
        <Link to="/parametres">Paramètres › Prix</Link>. Le taux retenu est{' '}
        <strong>figé sur la pièce au chiffrage</strong> : changer un taux ne modifie jamais
        un devis déjà chiffré.
      </p>

      {/* ── LA VRAIE DÉCISION PAR AFFAIRE — coefficient de sécurité ──
          « Ce client-là, je prends 15 % de plus. » Ce n'est PAS le
          retour de la marge globale supprimée le 5 août : celle-là était
          un réglage général qui devait rester à 1, donc un piège.
          Celui-ci est par pièce, vaut 1 tant que personne ne l'écrit, et
          la synthèse affiche sa ligne avec le montant qu'il ajoute. */}
      {onCoefficient ? (
        <div className="grid-2">
          <NumberField
            label={`Coefficient de sécurité — ${niveau}`}
            value={coefficient ?? null}
            min={0}
            step="0.01"
            disabled={disabled}
            hint="Vide = 1. « 1,15 » ajoute 15 % à l’usinage — jamais à la matière ni à la sous-traitance, qui sont de la revente."
            onChange={(v) => onCoefficient(v)}
          />
        </div>
      ) : null}
    </div>
  );
}

export default TauxBloc;
